import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sun, Moon, Menu, X, PenSquare } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("fb_theme") || "light");

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    window.__setTheme?.(next);
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/subscribe", label: "Subscribe" },
    { href: "/referrals", label: "Refer & Earn" },
    { href: "/hire", label: "Hire Elizabeth" },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "backdrop-blur-xl bg-background/90 border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="font-cormorant text-2xl font-semibold tracking-tight text-foreground group-hover:text-brand-accent transition-colors duration-200">
            Freelanceblogger
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-manrope text-sm font-medium transition-colors duration-200 ${
                isActive(link.href)
                  ? "text-brand-accent"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Side */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/admin"
                data-testid="admin-dashboard-link"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium font-manrope text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
              >
                <PenSquare size={15} />
                Dashboard
              </Link>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="px-3 py-1.5 rounded-md text-sm font-medium font-manrope text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          ) : (
            <Link
              to="/subscribe"
              data-testid="nav-subscribe-cta"
              className="px-4 py-2 rounded-full bg-brand-accent text-white text-sm font-medium font-manrope hover:bg-brand-accent-hover transition-colors duration-200"
            >
              Subscribe Free
            </Link>
          )}
        </div>

        {/* Mobile Toggle */}
        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="p-2 rounded-md text-muted-foreground hover:text-foreground transition-colors"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl px-4 pb-4 pt-2">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`block py-3 font-manrope text-sm font-medium border-b border-border last:border-0 transition-colors ${
                isActive(link.href) ? "text-brand-accent" : "text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/admin" className="block py-3 font-manrope text-sm font-medium border-b border-border text-foreground">
                Dashboard
              </Link>
              <button onClick={logout} className="block py-3 font-manrope text-sm font-medium text-muted-foreground">
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/subscribe"
              className="block mt-3 text-center px-4 py-2 rounded-full bg-brand-accent text-white text-sm font-medium font-manrope"
            >
              Subscribe Free
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
